import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fetchRejectionsByRange } from "../api/RejectionsApi";
import "../styles/RejectionsAnalytics.css";

// ─────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) : "0.0");
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

function downloadCSV(data, filename) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [
    keys.join(","),
    ...data.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const getRecordFields = (r) => ({
  id: r.id,
  farm_name: r.farm_name || r.farm_details?.name || "Unknown",
  farm_id: r.farm,
  ghse_name: r.ghse_name || r.ghse_details?.name || `GHSE ${r.ghse}` || "Unknown",
  ghse_id: r.ghse,
  zone_name: r.ghse_details?.zone_name || r.zone_name || "—",
  variety_name: r.variety_name || r.variety_details?.name || `Variety ${r.variety}` || "Unknown",
  variety_id: r.variety,
  stems_per_net: r.variety_details?.stems_per_net || null,
  reason: r.reason || "Unspecified",
  rejected_stems: parseInt(r.rejected_stems) || 0,
  comment: r.comment || "",
  captured_at: r.captured_at || null,
  session: r.session,
  captured_by: r.captured_by,
  harvest_type: r.harvest_type_details?.name || (r.harvest_type ? `Type ${r.harvest_type}` : "—"),
  harvest_type_id: r.harvest_type,
});

// ─────────────────────────────────────────────
// PRESET DATE RANGES
// ─────────────────────────────────────────────
const DATE_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Yesterday", days: -1, yesterday: true },
  { label: "Last 7 Days", days: 7 },
  { label: "Last 14 Days", days: 14 },
  { label: "Last 30 Days", days: 30 },
  { label: "This Month", thisMonth: true },
];

function getPresetRange(preset) {
  const today = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (preset.yesterday) {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { dateFrom: fmt(y), dateTo: fmt(y) };
  }
  if (preset.thisMonth) {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { dateFrom: fmt(start), dateTo: fmt(today) };
  }
  const from = new Date(today); from.setDate(today.getDate() - preset.days);
  return { dateFrom: fmt(from), dateTo: fmt(today) };
}

// ─────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon, accent, trend }) => (
  <div className="ra-stat" style={{ "--accent": accent }}>
    <div className="ra-stat-icon">{icon}</div>
    <div className="ra-stat-body">
      <div className="ra-stat-val">{value}</div>
      <div className="ra-stat-label">{label}</div>
      {sub && <div className="ra-stat-sub">{sub}</div>}
    </div>
    {trend !== undefined && (
      <div className={`ra-stat-trend ${trend >= 0 ? "up" : "down"}`}>
        {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
      </div>
    )}
  </div>
);

const RankBar = ({ name, stems, max, rank, total, sub }) => {
  const w = max > 0 ? (stems / max) * 100 : 0;
  const share = pct(stems, total);
  const RANK_COLORS = {
    1: "var(--brand-pink)",
    2: "var(--brand-olive)",
    3: "#e07b3f",
  };
  const color = RANK_COLORS[rank] || "var(--muted-bar)";
  return (
    <div className="ra-rank-row">
      <div className="ra-rank-meta">
        <span className={`ra-rank-badge rank-${rank <= 3 ? rank : "n"}`}>
          {rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
        </span>
        <div className="ra-rank-info">
          <span className="ra-rank-name">{name}</span>
          {sub && <span className="ra-rank-sub">{sub}</span>}
        </div>
      </div>
      <div className="ra-rank-track">
        <div className="ra-rank-fill" style={{ width: `${w}%`, background: color }} />
      </div>
      <div className="ra-rank-nums">
        <span className="ra-rank-stems">{fmt(stems)}</span>
        <span className="ra-rank-pct">{share}%</span>
      </div>
    </div>
  );
};

const MiniChip = ({ label, active, onClick }) => (
  <button className={`ra-chip ${active ? "ra-chip-active" : ""}`} onClick={onClick}>
    {label}
  </button>
);

const DateSlicer = ({ dateFrom, dateTo, onChange, onApply }) => {
  const [open, setOpen] = useState(false);
  const activePreset = DATE_PRESETS.find((p) => {
    const r = getPresetRange(p);
    return r.dateFrom === dateFrom && r.dateTo === dateTo;
  });

  return (
    <div className="ra-date-slicer">
      <div className="ra-date-trigger" onClick={() => setOpen((v) => !v)}>
        <span className="ra-date-icon">📅</span>
        <div className="ra-date-display">
          <span className="ra-date-preset-label">{activePreset?.label || "Custom Range"}</span>
          <span className="ra-date-range-text">
            {dateFrom && dateTo
              ? `${dateFrom} → ${dateTo}`
              : "Select date range"}
          </span>
        </div>
        <span className={`ra-date-arrow ${open ? "open" : ""}`}>▾</span>
      </div>

      {open && (
        <div className="ra-date-panel">
          <div className="ra-date-presets">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                className={`ra-preset-btn ${activePreset?.label === p.label ? "active" : ""}`}
                onClick={() => { onChange(getPresetRange(p)); setOpen(false); }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="ra-date-divider">— or pick custom —</div>
          <div className="ra-date-inputs">
            <div className="ra-date-field">
              <label>From</label>
              <input type="date" value={dateFrom} onChange={(e) => onChange({ dateFrom: e.target.value, dateTo })} />
            </div>
            <div className="ra-date-field">
              <label>To</label>
              <input type="date" value={dateTo} onChange={(e) => onChange({ dateFrom, dateTo: e.target.value })} />
            </div>
          </div>
          <button className="ra-date-apply" onClick={() => { onApply(); setOpen(false); }}>
            Apply & Refresh ✓
          </button>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="ra-empty">
    <div className="ra-empty-icon">🌿</div>
    <div className="ra-empty-msg">{message || "No data available for the selected filters."}</div>
  </div>
);

// ─────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────
const RejectionsAnalytics = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [filters, setFilters] = useState({
    dateFrom: "", dateTo: "", farm: "", ghse: "", variety: "", reason: "", harvest_type: "",
  });
  const [showFilters, setShowFilters] = useState(true);
  const [sortCol, setSortCol] = useState("rejected_stems");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Initialize default date range (Last 7 Days)
  useEffect(() => {
    const defaultRange = getPresetRange(DATE_PRESETS[2]); // "Last 7 Days"
    setFilters((prev) => ({
      ...prev,
      dateFrom: defaultRange.dateFrom,
      dateTo: defaultRange.dateTo,
    }));
  }, []);

  // Load data from backend when date range changes
  const loadData = useCallback(async () => {
    if (!filters.dateFrom || !filters.dateTo) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetchRejectionsByRange(
        filters.dateFrom,
        filters.dateTo,
        filters.farm || undefined
      );
      const rawRec = response?.results || [];
      setRecords(rawRec.map(getRecordFields));
    } catch (e) {
      setError("Failed to load rejection data. Please try again.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo, filters.farm]);

  // Fetch data on mount and when date range changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  const upd = useCallback((k, v) => setFilters((p) => ({ ...p, [k]: v })), []);

  const resetFilters = () => {
    const defaultRange = getPresetRange(DATE_PRESETS[2]);
    setFilters({ dateFrom: defaultRange.dateFrom, dateTo: defaultRange.dateTo, farm: "", ghse: "", variety: "", reason: "", harvest_type: "" });
    setPage(1);
  };

  const farms = useMemo(() => [...new Set(records.map((r) => r.farm_name).filter(Boolean))].sort(), [records]);
  const ghses = useMemo(() => {
    const src = filters.farm ? records.filter((r) => r.farm_name === filters.farm) : records;
    return [...new Set(src.map((r) => r.ghse_name).filter(Boolean))].sort();
  }, [records, filters.farm]);
  const varieties = useMemo(() => {
    let src = records;
    if (filters.farm) src = src.filter((r) => r.farm_name === filters.farm);
    if (filters.ghse) src = src.filter((r) => r.ghse_name === filters.ghse);
    return [...new Set(src.map((r) => r.variety_name).filter(Boolean))].sort();
  }, [records, filters.farm, filters.ghse]);
  const reasons = useMemo(() => [...new Set(records.map((r) => r.reason).filter(Boolean))].sort(), [records]);
  const harvestTypes = useMemo(() => [...new Set(records.map((r) => r.harvest_type).filter((ht) => ht && ht !== "—"))].sort(), [records]);

  const handleFarmChange = (val) => {
    setFilters((p) => ({ ...p, farm: val, ghse: "", variety: "" }));
    setPage(1);
  };
  const handleGhseChange = (val) => {
    setFilters((p) => ({ ...p, ghse: val, variety: "" }));
    setPage(1);
  };

  // Client-side filtering (date already filtered by backend, but keep for safety)
  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (filters.farm && r.farm_name !== filters.farm) return false;
      if (filters.ghse && r.ghse_name !== filters.ghse) return false;
      if (filters.variety && r.variety_name !== filters.variety) return false;
      if (filters.reason && !r.reason.toLowerCase().includes(filters.reason.toLowerCase())) return false;
      if (filters.harvest_type && r.harvest_type !== filters.harvest_type) return false;
      return true;
    });
  }, [records, filters]);

  const activeFilterCount = [filters.farm, filters.ghse, filters.variety, filters.reason, filters.harvest_type].filter(Boolean).length;

  const totalRejected = useMemo(() => filtered.reduce((s, r) => s + r.rejected_stems, 0), [filtered]);
  const avgPerEvent = useMemo(() => filtered.length ? Math.round(totalRejected / filtered.length) : 0, [totalRejected, filtered]);

  const byReason = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!map[r.reason]) map[r.reason] = { reason: r.reason, stems: 0, count: 0 };
      map[r.reason].stems += r.rejected_stems;
      map[r.reason].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byFarm = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!map[r.farm_name]) map[r.farm_name] = { farm: r.farm_name, stems: 0, count: 0 };
      map[r.farm_name].stems += r.rejected_stems;
      map[r.farm_name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byGHSE = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = r.ghse_name;
      if (!map[key]) map[key] = { ghse: key, farm: r.farm_name, zone: r.zone_name, stems: 0, count: 0 };
      map[key].stems += r.rejected_stems;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byVariety = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!map[r.variety_name]) map[r.variety_name] = {
        variety: r.variety_name, stems: 0, count: 0,
        stems_per_net: r.stems_per_net,
      };
      map[r.variety_name].stems += r.rejected_stems;
      map[r.variety_name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byZone = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!map[r.zone_name]) map[r.zone_name] = { zone: r.zone_name, stems: 0, count: 0 };
      map[r.zone_name].stems += r.rejected_stems;
      map[r.zone_name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byHarvestType = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = r.harvest_type || "Unspecified";
      if (!map[key]) map[key] = { harvest_type: key, stems: 0, count: 0 };
      map[key].stems += r.rejected_stems;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  const paged = sortedFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(sortedFiltered.length / PAGE_SIZE);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("desc"); }
    setPage(1);
  };

  const SortIcon = ({ col }) =>
    sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕";

  if (loading) return (
    <div className="ra-loading">
      <div className="ra-spinner-wrap">
        <div className="ra-spinner"></div>
        <div className="ra-spinner-ring"></div>
      </div>
      <p className="ra-loading-text">Loading Rejection Analytics…</p>
    </div>
  );

  if (error) return (
    <div className="ra-error-state">
      <div className="ra-error-icon">⚠️</div>
      <p>{error}</p>
      <button onClick={loadData}>Retry</button>
    </div>
  );

  const TABS = [
    { id: "overview",    label: "Overview",    icon: "📊" },
    { id: "reasons",     label: "By Reason",   icon: "⚠️" },
    { id: "greenhouse",  label: "By GHSE",     icon: "🏠" },
    { id: "variety",     label: "By Variety",  icon: "🌿" },
    { id: "zone",        label: "By Zone",     icon: "📍" },
    { id: "harvest",     label: "By Harvest",  icon: "🌾" },
    { id: "table",       label: "Raw Data",    icon: "📋" },
  ];

  return (
    <div className="ra-root">

      {/* ── HEADER ── */}
      <header className="ra-header">
        <div className="ra-header-left">
          <div className="ra-logo-wrap">
            <span className="ra-logo-icon">🌸</span>
          </div>
          <div className="ra-header-titles">
            <h1 className="ra-title">Rejections Analytics</h1>
            <p className="ra-subtitle">Track · Analyse · Reduce · Improve</p>
          </div>
        </div>
        <div className="ra-header-right">
          <div className="ra-record-count">
            <span className="ra-rc-num">{fmt(filtered.length)}</span>
            <span className="ra-rc-label">records</span>
          </div>
          <button
            className={`ra-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters((p) => !p)}
          >
            <span>⚙</span>
            Filters
            {activeFilterCount > 0 && <span className="ra-badge">{activeFilterCount}</span>}
          </button>
          <button className="ra-export-btn" onClick={() => downloadCSV(filtered.map(r => ({
            Date: fmtDate(r.captured_at),
            Farm: r.farm_name,
            GHSE: r.ghse_name,
            Zone: r.zone_name,
            Variety: r.variety_name,
            Reason: r.reason,
            "Harvest Type": r.harvest_type,
            "Rejected Stems": r.rejected_stems,
            Comment: r.comment,
          })), "rejections_export.csv")}>
            ⬇ Export CSV
          </button>
        </div>
      </header>

      {/* ── FILTERS ── */}
      {showFilters && (
        <div className="ra-filters">
          <div className="ra-filters-inner">
            <div className="ra-filter-group ra-filter-date-group">
              <label className="ra-filter-label">📅 Date Range</label>
              <DateSlicer
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                onChange={({ dateFrom, dateTo }) => setFilters((p) => ({ ...p, dateFrom, dateTo }))}
                onApply={loadData}
              />
            </div>

            <div className="ra-filter-group">
              <label className="ra-filter-label">🚜 Farm</label>
              <div className="ra-select-wrap">
                <select value={filters.farm} onChange={(e) => handleFarmChange(e.target.value)}>
                  <option value="">All Farms</option>
                  {farms.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <span className="ra-select-arrow">▾</span>
              </div>
            </div>

            <div className="ra-filter-group">
              <label className="ra-filter-label">🏠 Greenhouse</label>
              <div className="ra-select-wrap">
                <select value={filters.ghse} onChange={(e) => handleGhseChange(e.target.value)}>
                  <option value="">All GHSEs</option>
                  {ghses.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <span className="ra-select-arrow">▾</span>
              </div>
            </div>

            <div className="ra-filter-group">
              <label className="ra-filter-label">🌿 Variety</label>
              <div className="ra-select-wrap">
                <select value={filters.variety} onChange={(e) => upd("variety", e.target.value)}>
                  <option value="">All Varieties</option>
                  {varieties.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <span className="ra-select-arrow">▾</span>
              </div>
            </div>

            <div className="ra-filter-group">
              <label className="ra-filter-label">⚠️ Reason</label>
              <div className="ra-select-wrap">
                <select value={filters.reason} onChange={(e) => upd("reason", e.target.value)}>
                  <option value="">All Reasons</option>
                  {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <span className="ra-select-arrow">▾</span>
              </div>
            </div>

            <div className="ra-filter-group">
              <label className="ra-filter-label">🌾 Harvest Type</label>
              <div className="ra-select-wrap">
                <select value={filters.harvest_type} onChange={(e) => { upd("harvest_type", e.target.value); setPage(1); }}>
                  <option value="">All Harvest Types</option>
                  {harvestTypes.map((ht) => <option key={ht} value={ht}>{ht}</option>)}
                </select>
                <span className="ra-select-arrow">▾</span>
              </div>
            </div>

            <button className="ra-reset-btn" onClick={resetFilters}>✕ Reset</button>
          </div>

          <div className="ra-quick-dates">
            {DATE_PRESETS.map((p) => {
              const r = getPresetRange(p);
              const active = r.dateFrom === filters.dateFrom && r.dateTo === filters.dateTo;
              return (
                <MiniChip
                  key={p.label}
                  label={p.label}
                  active={active}
                  onClick={() => {
                    setFilters((f) => ({ ...f, dateFrom: r.dateFrom, dateTo: r.dateTo }));
                    setPage(1);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── TABS ── */}
      <div className="ra-tabs-bar">
        <div className="ra-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`ra-tab ${tab === t.id ? "ra-tab-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <span className="ra-tab-icon">{t.icon}</span>
              <span className="ra-tab-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="ra-content">

        {/* ════════ OVERVIEW ════════ */}
        {tab === "overview" && (
          <div className="ra-section">
            <div className="ra-stats-grid">
              <StatCard label="Total Rejected Stems" value={fmt(totalRejected)} icon="🌹" accent="var(--brand-pink)" sub={`${fmt(filtered.length)} events`} />
              <StatCard label="Avg Stems / Event" value={fmt(avgPerEvent)} icon="📉" accent="var(--brand-olive)" />
              <StatCard label="Unique Reasons" value={byReason.length} icon="⚠️" accent="#e07b3f" sub="rejection causes" />
              <StatCard label="Affected GHSEs" value={byGHSE.length} icon="🏠" accent="#4b8ca8" sub={`across ${byFarm.length} farm(s)`} />
              <StatCard label="Affected Varieties" value={byVariety.length} icon="🌿" accent="#6fa84b" />
            </div>

            {byReason.length > 0 && (
              <div className="ra-alert">
                <div className="ra-alert-stripe"></div>
                <span className="ra-alert-icon">⚠️</span>
                <div className="ra-alert-body">
                  <div className="ra-alert-title">Top Rejection Cause</div>
                  <div className="ra-alert-text">
                    <strong>{byReason[0].reason}</strong> — {fmt(byReason[0].stems)} stems rejected
                    ({pct(byReason[0].stems, totalRejected)}% of all rejections across {byReason[0].count} events)
                  </div>
                </div>
              </div>
            )}

            <div className="ra-two-col">
              <div className="ra-panel">
                <div className="ra-panel-hdr">
                  <h3>⚠️ Top Reasons</h3>
                  <button className="ra-dl-btn" onClick={() => downloadCSV(byReason, "by_reason.csv")}>⬇ CSV</button>
                </div>
                {byReason.length === 0 ? <EmptyState /> : byReason.slice(0, 8).map((r, i) => (
                  <RankBar key={r.reason} name={r.reason} stems={r.stems}
                    max={byReason[0].stems} rank={i + 1} total={totalRejected}
                    sub={`${r.count} events · avg ${fmt(Math.round(r.stems / r.count))}/event`} />
                ))}
              </div>

              <div className="ra-panel">
                <div className="ra-panel-hdr">
                  <h3>🏠 Top Greenhouses</h3>
                  <button className="ra-dl-btn" onClick={() => downloadCSV(byGHSE, "by_ghse.csv")}>⬇ CSV</button>
                </div>
                {byGHSE.length === 0 ? <EmptyState /> : byGHSE.slice(0, 8).map((g, i) => (
                  <RankBar key={g.ghse} name={g.ghse} stems={g.stems}
                    max={byGHSE[0].stems} rank={i + 1} total={totalRejected}
                    sub={`${g.farm} · ${g.zone} · ${g.count} events`} />
                ))}
              </div>
            </div>

            <div className="ra-panel">
              <div className="ra-panel-hdr">
                <h3>🚜 Farm Overview</h3>
                <button className="ra-dl-btn" onClick={() => downloadCSV(byFarm, "by_farm.csv")}>⬇ CSV</button>
              </div>
              {byFarm.length === 0 ? <EmptyState /> : (
                <div className="ra-farm-grid">
                  {byFarm.map((f) => (
                    <div key={f.farm} className="ra-farm-card">
                      <div className="ra-farm-header">
                        <span className="ra-farm-icon">🚜</span>
                        <span className="ra-farm-name">{f.farm}</span>
                      </div>
                      <div className="ra-farm-big">{fmt(f.stems)}</div>
                      <div className="ra-farm-label">rejected stems</div>
                      <div className="ra-farm-bar-wrap">
                        <div className="ra-farm-bar-fill" style={{ width: `${pct(f.stems, totalRejected)}%` }}></div>
                      </div>
                      <div className="ra-farm-meta">
                        <span>{fmt(f.count)} events</span>
                        <span>{pct(f.stems, totalRejected)}% of total</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ra-panel">
              <div className="ra-panel-hdr">
                <h3>🌿 Top Varieties Affected</h3>
              </div>
              <div className="ra-table-wrap">
                <table className="ra-table">
                  <thead>
                    <tr><th>#</th><th>Variety</th><th>Rejected Stems</th><th>Events</th><th>Avg/Event</th><th>Share</th></tr>
                  </thead>
                  <tbody>
                    {byVariety.slice(0, 5).map((v, i) => (
                      <tr key={v.variety} className={i < 3 ? "ra-top-row" : ""}>
                        <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                        <td className="ra-name-cell">{v.variety}</td>
                        <td className="ra-num">{fmt(v.stems)}</td>
                        <td className="ra-num">{v.count}</td>
                        <td className="ra-num">{v.count ? fmt(Math.round(v.stems / v.count)) : "—"}</td>
                        <td>
                          <div className="ra-inline-bar">
                            <div className="ra-inline-fill" style={{ width: `${pct(v.stems, totalRejected)}%` }}></div>
                            <span>{pct(v.stems, totalRejected)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ════════ BY REASON ════════ */}
        {tab === "reasons" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>⚠️ Rejection Reasons — Full Breakdown</h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(byReason, "by_reason.csv")}>⬇ Download CSV</button>
            </div>
            {byReason.length === 0 ? <EmptyState /> : (
              <>
                <div className="ra-reason-cards">
                  {byReason.slice(0, 3).map((r, i) => (
                    <div key={r.reason} className={`ra-reason-card ra-reason-card-${i + 1}`}>
                      <div className="ra-reason-rank">{["🥇", "🥈", "🥉"][i]}</div>
                      <div className="ra-reason-name-big">{r.reason}</div>
                      <div className="ra-reason-stems-big">{fmt(r.stems)}</div>
                      <div className="ra-reason-pct-big">{pct(r.stems, totalRejected)}%</div>
                      <div className="ra-reason-events">{r.count} events</div>
                    </div>
                  ))}
                </div>
                <div className="ra-table-wrap">
                  <table className="ra-table">
                    <thead>
                      <tr>
                        <th>Rank</th><th>Reason</th><th>Rejected Stems</th>
                        <th>Events</th><th>Avg / Event</th><th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byReason.map((r, i) => (
                        <tr key={r.reason} className={i < 3 ? "ra-top-row" : ""}>
                          <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                          <td className="ra-name-cell">{r.reason}</td>
                          <td className="ra-num">{fmt(r.stems)}</td>
                          <td className="ra-num">{r.count}</td>
                          <td className="ra-num">{r.count ? fmt(Math.round(r.stems / r.count)) : "—"}</td>
                          <td>
                            <div className="ra-inline-bar">
                              <div className="ra-inline-fill" style={{ width: `${pct(r.stems, totalRejected)}%` }}></div>
                              <span>{pct(r.stems, totalRejected)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════ BY GHSE ════════ */}
        {tab === "greenhouse" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>🏠 Rejections Per Greenhouse</h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(byGHSE, "by_ghse.csv")}>⬇ Download CSV</button>
            </div>
            {byGHSE.length === 0 ? <EmptyState /> : (
              <div className="ra-table-wrap">
                <table className="ra-table">
                  <thead>
                    <tr><th>Rank</th><th>GHSE</th><th>Farm</th><th>Zone</th><th>Stems</th><th>Events</th><th>Avg/Event</th><th>Share</th></tr>
                  </thead>
                  <tbody>
                    {byGHSE.map((g, i) => (
                      <tr key={g.ghse} className={i < 3 ? "ra-top-row" : ""}>
                        <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                        <td className="ra-name-cell">{g.ghse}</td>
                        <td>{g.farm}</td>
                        <td className="ra-muted">{g.zone}</td>
                        <td className="ra-num">{fmt(g.stems)}</td>
                        <td className="ra-num">{g.count}</td>
                        <td className="ra-num">{g.count ? fmt(Math.round(g.stems / g.count)) : "—"}</td>
                        <td>
                          <div className="ra-inline-bar">
                            <div className="ra-inline-fill" style={{ width: `${pct(g.stems, totalRejected)}%` }}></div>
                            <span>{pct(g.stems, totalRejected)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ════════ BY VARIETY ════════ */}
        {tab === "variety" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>🌿 Rejections Per Variety</h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(byVariety, "by_variety.csv")}>⬇ Download CSV</button>
            </div>
            {byVariety.length === 0 ? <EmptyState /> : (
              <div className="ra-table-wrap">
                <table className="ra-table">
                  <thead>
                    <tr><th>Rank</th><th>Variety</th><th>Stems/Net</th><th>Rejected</th><th>Events</th><th>Avg/Event</th><th>Nets Lost</th><th>Share</th></tr>
                  </thead>
                  <tbody>
                    {byVariety.map((v, i) => (
                      <tr key={v.variety} className={i < 3 ? "ra-top-row" : ""}>
                        <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                        <td className="ra-name-cell">{v.variety}</td>
                        <td className="ra-num ra-muted">{v.stems_per_net || "—"}</td>
                        <td className="ra-num">{fmt(v.stems)}</td>
                        <td className="ra-num">{v.count}</td>
                        <td className="ra-num">{v.count ? fmt(Math.round(v.stems / v.count)) : "—"}</td>
                        <td className="ra-num ra-muted">
                          {v.stems_per_net ? (v.stems / v.stems_per_net).toFixed(1) : "—"}
                        </td>
                        <td>
                          <div className="ra-inline-bar">
                            <div className="ra-inline-fill" style={{ width: `${pct(v.stems, totalRejected)}%` }}></div>
                            <span>{pct(v.stems, totalRejected)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ════════ BY ZONE ════════ */}
        {tab === "zone" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>📍 Rejections Per Zone</h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(byZone, "by_zone.csv")}>⬇ Download CSV</button>
            </div>
            {byZone.length === 0 ? <EmptyState /> : (
              <div className="ra-table-wrap">
                <table className="ra-table">
                  <thead>
                    <tr><th>Rank</th><th>Zone</th><th>Rejected Stems</th><th>Events</th><th>Avg/Event</th><th>Share</th></tr>
                  </thead>
                  <tbody>
                    {byZone.map((z, i) => (
                      <tr key={z.zone} className={i < 3 ? "ra-top-row" : ""}>
                        <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                        <td className="ra-name-cell">{z.zone}</td>
                        <td className="ra-num">{fmt(z.stems)}</td>
                        <td className="ra-num">{z.count}</td>
                        <td className="ra-num">{z.count ? fmt(Math.round(z.stems / z.count)) : "—"}</td>
                        <td>
                          <div className="ra-inline-bar">
                            <div className="ra-inline-fill" style={{ width: `${pct(z.stems, totalRejected)}%` }}></div>
                            <span>{pct(z.stems, totalRejected)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ════════ BY HARVEST TYPE ════════ */}
        {tab === "harvest" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>🌾 Rejections Per Harvest Type</h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(byHarvestType, "by_harvest_type.csv")}>⬇ Download CSV</button>
            </div>
            {byHarvestType.length === 0 ? <EmptyState /> : (
              <>
                <div className="ra-reason-cards">
                  {byHarvestType.slice(0, 3).map((h, i) => (
                    <div key={h.harvest_type} className={`ra-reason-card ra-reason-card-${i + 1}`}>
                      <div className="ra-reason-rank">{["🥇", "🥈", "🥉"][i]}</div>
                      <div className="ra-reason-name-big">{h.harvest_type}</div>
                      <div className="ra-reason-stems-big">{fmt(h.stems)}</div>
                      <div className="ra-reason-pct-big">{pct(h.stems, totalRejected)}%</div>
                      <div className="ra-reason-events">{h.count} events</div>
                    </div>
                  ))}
                </div>
                <div className="ra-table-wrap">
                  <table className="ra-table">
                    <thead>
                      <tr>
                        <th>Rank</th><th>Harvest Type</th><th>Rejected Stems</th>
                        <th>Events</th><th>Avg / Event</th><th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byHarvestType.map((h, i) => (
                        <tr key={h.harvest_type} className={i < 3 ? "ra-top-row" : ""}>
                          <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                          <td className="ra-name-cell">{h.harvest_type}</td>
                          <td className="ra-num">{fmt(h.stems)}</td>
                          <td className="ra-num">{h.count}</td>
                          <td className="ra-num">{h.count ? fmt(Math.round(h.stems / h.count)) : "—"}</td>
                          <td>
                            <div className="ra-inline-bar">
                              <div className="ra-inline-fill" style={{ width: `${pct(h.stems, totalRejected)}%` }}></div>
                              <span>{pct(h.stems, totalRejected)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════ RAW DATA ════════ */}
        {tab === "table" && (
          <div className="ra-section">
            <div className="ra-section-header">
              <h2>📋 All Rejection Records <span className="ra-count-badge">{fmt(filtered.length)}</span></h2>
              <button className="ra-dl-btn" onClick={() => downloadCSV(sortedFiltered, "all_rejections.csv")}>⬇ Export All</button>
            </div>
            {filtered.length === 0 ? <EmptyState /> : (
              <>
                <div className="ra-table-wrap">
                  <table className="ra-table ra-table-sortable">
                    <thead>
                      <tr>
                        {[
                          { col: "captured_at", label: "Date/Time" },
                          { col: "farm_name", label: "Farm" },
                          { col: "ghse_name", label: "GHSE" },
                          { col: "zone_name", label: "Zone" },
                          { col: "variety_name", label: "Variety" },
                          { col: "reason", label: "Reason" },
                          { col: "harvest_type", label: "Harvest Type" },
                          { col: "rejected_stems", label: "Stems" },
                          { col: "comment", label: "Comment" },
                        ].map(({ col, label }) => (
                          <th key={col} onClick={() => handleSort(col)} className="ra-sortable">
                            {label}<SortIcon col={col} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((r, i) => (
                        <tr key={r.id || i}>
                          <td className="ra-date-cell">
                            <div>{fmtDate(r.captured_at)}</div>
                            <div className="ra-time">{fmtTime(r.captured_at)}</div>
                          </td>
                          <td><span className="ra-farm-pill">{r.farm_name}</span></td>
                          <td>{r.ghse_name}</td>
                          <td className="ra-muted">{r.zone_name}</td>
                          <td className="ra-name-cell">{r.variety_name}</td>
                          <td>{r.reason}</td>
                          <td>{r.harvest_type}</td>
                          <td className="ra-num ra-stems-cell">{fmt(r.rejected_stems)}</td>
                          <td className="ra-comment">{r.comment || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="ra-pagination">
                    <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                    <span className="ra-page-info">Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>

      {/* ── FOOTER ── */}
      <footer className="ra-footer">
        <span>Rejections Analytics · {new Date().getFullYear()}</span>
        <span>{fmt(filtered.length)} records · {fmt(totalRejected)} stems rejected</span>
      </footer>
    </div>
  );
};

export default RejectionsAnalytics;